import { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { trackSignupComplete } from '../../utils/gtm';
import { demoSessionClient } from '../../lib/demo-session-client';
import { supabase } from '../../lib/supabase-client';
import { publicAnonKey } from '../../../utils/supabase/info';
import { toast } from 'sonner';
import { fetchRegistrationCheckboxes, RegistrationCheckboxes } from '../../lib/prompt-admin-api';

export function RegisterPage() {
    const { user, signUp, signIn, signInWithGoogle, loading: authLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [checkboxTexts, setCheckboxTexts] = useState<RegistrationCheckboxes | null>(null);

    useEffect(() => {
        fetchRegistrationCheckboxes().then(setCheckboxTexts);
    }, []);

    // Demo session params
    const isFromDemo = searchParams.get('demo') === 'true';
    const returnUrl = searchParams.get('returnUrl') || '/';

    // Redirect if already logged in
    if (user && !authLoading) {
        return <Navigate to="/" replace />;
    }

    const validatePassword = () => {
        if (password.length < 8) {
            return 'Hasło musi mieć co najmniej 8 znaków';
        }
        if (password !== confirmPassword) {
            return 'Hasła nie są zgodne';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!tosAccepted) {
            setError('Musisz zaakceptować regulamin aby kontynuować');
            return;
        }

        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        const { error } = await signUp(email, password, newsletterSubscribed);

        if (error) {
            let errorMessage = error.message;
            if (errorMessage === 'User already registered') {
                errorMessage = 'Konto z tym adresem email już istnieje';
            } else if (errorMessage.includes('Password should contain at least one character of each')) {
                errorMessage = 'Hasło musi zawierać małą i dużą literę oraz cyfrę.';
            }

            setError(errorMessage);
            setLoading(false);
            return;
        }

        // Track successful signup
        trackSignupComplete(email, 'free');

        // If from demo, auto sign in and claim session
        if (isFromDemo) {
            try {
                // Auto sign in
                const { error: signInError } = await signIn(email, password);
                if (signInError) {
                    console.error('Auto sign-in failed:', signInError);
                    setSuccess(true);
                    setLoading(false);
                    return;
                }

                // Wait a moment for auth to settle
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Get session token from URL (not localStorage)
                const sessionToken = searchParams.get('session');
                if (sessionToken) {
                    // Get access token - retry a few times if needed
                    let session = null;
                    for (let i = 0; i < 3; i++) {
                        const { data } = await supabase.auth.getSession();
                        session = data.session;
                        if (session?.access_token && session?.user?.id) {
                            console.log('[Demo] Session ready, user:', session.user.id);
                            break;
                        }
                        console.log('[Demo] Waiting for session, attempt:', i + 1);
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }

                    if (session?.access_token) {
                        console.log('[Demo] Access token length:', session.access_token.length);
                        console.log('[Demo] User ID:', session.user?.id);

                        try {
                            console.log('[Demo] Starting demo claim process...');
                            toast.info('Przetwarzanie sesji demo...');

                            // Step 1: Claim session (transfer files, deduct credit)
                            console.log('[Demo] Calling claim with token:', sessionToken);
                            const claimResult = await demoSessionClient.claim(sessionToken, session.access_token);
                            console.log('[Demo] Claim result:', claimResult);
                            toast.success('Sesja demo została przypisana! Generowanie renderów...');

                            // Step 2: Create "Onboarding" property
                            const { data: property, error: propError } = await supabase
                                .from('properties')
                                .insert({
                                    user_id: session.user.id,
                                    name: 'Onboarding',
                                    address: '',
                                })
                                .select()
                                .single();

                            if (propError || !property) {
                                console.error('Failed to create Onboarding property:', propError);
                                throw new Error('Nie udało się utworzyć folderu Onboarding');
                            }

                            // Step 3: Generate render from claimed files
                            const renders = claimResult.renders;
                            if (renders && renders.length > 0) {
                                const render = renders[0];

                                // Call /generate endpoint
                                const generateResponse = await fetch(
                                    `https://uyavxanmcwfelwqwsesr.supabase.co/functions/v1/demo-session/generate`,
                                    {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${publicAnonKey}`, // Use ANON key for Gateway
                                            'apikey': publicAnonKey,
                                        },
                                        body: JSON.stringify({
                                            sessionToken,
                                            accessToken: session.access_token, // Pass user token in body
                                            gcsPath: render.original_gcs_path,
                                            mode: 'enhance',
                                            settings: {},
                                            roomName: 'Pokój',
                                        }),
                                    }
                                );

                                if (!generateResponse.ok) {
                                    throw new Error('Generowanie nie powiodło się');
                                }

                                const generateResult = await generateResponse.json();
                                console.log('[Demo] Generate result:', generateResult);

                                // Step 4: Save to database - both original and generated variants
                                // Save original image and capture its ID
                                const { data: originalImage, error: originalError } = await supabase
                                    .from('images')
                                    .insert({
                                        user_id: session.user.id,
                                        property_id: property.id,
                                        file_name: `demo_original_${Date.now()}.jpg`,
                                        gcs_path: render.original_gcs_path,
                                        image_type: 'original',
                                    })
                                    .select()
                                    .single();

                                if (originalError || !originalImage) {
                                    console.error('Failed to save original image:', originalError);
                                    throw new Error('Failed to save original image');
                                }

                                console.log('[Demo] Saved original image:', originalImage.id);

                                // Save all generated variants from generateResult (NOT from claimResult which is empty!)
                                const variantImageIds: string[] = [];
                                console.log('[Demo] Generate result variants:', generateResult.variants);
                                if (generateResult.variants && generateResult.variants.length > 0) {
                                    for (let i = 0; i < generateResult.variants.length; i++) {
                                        const variant = generateResult.variants[i];
                                        const { data: variantImage, error: variantError } = await supabase
                                            .from('images')
                                            .insert({
                                                user_id: session.user.id,
                                                property_id: property.id,
                                                file_name: `demo_variant_${i + 1}_${Date.now()}.png`,
                                                gcs_path: variant.gcsPath,
                                                image_type: 'generated',
                                            })
                                            .select()
                                            .single();

                                        if (variantError || !variantImage) {
                                            console.error(`Failed to save variant ${i + 1}:`, variantError);
                                        } else {
                                            variantImageIds.push(variantImage.id);
                                            console.log(`[Demo] Saved variant ${i + 1}:`, variantImage.id);
                                        }
                                    }
                                }

                                // Step 5: Create generation record to link original with variants
                                const { data: generation, error: genError } = await supabase
                                    .from('generations')
                                    .insert({
                                        user_id: session.user.id,
                                        property_id: property.id,
                                        source_image_id: originalImage.id,
                                        generation_mode: 'enhance',
                                        status: 'completed',
                                        credits_used: 1, // Demo uses 1 credit
                                    })
                                    .select()
                                    .single();

                                if (genError || !generation) {
                                    console.error('Failed to create generation record:', genError);
                                } else {
                                    console.log('[Demo] Created generation:', generation.id);

                                    // Step 6: Create generation_outputs for each variant
                                    if (variantImageIds.length > 0) {
                                        const outputRecords = variantImageIds.map((variantId, idx) => ({
                                            generation_id: generation.id,
                                            output_image_id: variantId,
                                            ai_provider: 'gemini', // Gemini API used for demo
                                            variant_number: idx + 1,
                                        }));

                                        const { error: outputsError } = await supabase
                                            .from('generation_outputs')
                                            .insert(outputRecords);

                                        if (outputsError) {
                                            console.error('Failed to create generation outputs:', outputsError);
                                        } else {
                                            console.log('[Demo] Created generation outputs:', outputRecords.length);
                                        }
                                    }
                                }
                            }

                            toast.success('Render został wygenerowany i zapisany! 🎉');

                            // Clear demo session from localStorage
                            localStorage.removeItem('demo_session_token');
                            localStorage.removeItem('demo_session_expires');

                            // Redirect to gallery to see the new property
                            setTimeout(() => {
                                window.location.href = '/'; // Force full page reload to refresh all data
                            }, 2000);
                        } catch (claimError) {
                            if ((claimError as Error).message === 'insufficient_credits') {
                                toast.error('Nie masz wystarczających kredytów. Wykup subskrypcję aby odebrać render demo.');
                            } else {
                                console.error('Failed to claim session:', claimError);
                                toast.error('Błąd: ' + (claimError as Error).message);
                            }
                            // Still redirect to app
                            setTimeout(() => {
                                navigate(returnUrl);
                            }, 2000);
                        }
                    }
                }
            } catch (err) {
                console.error('Demo claim flow error:', err);
                setSuccess(true);
                setLoading(false);
            }
        } else {
            setSuccess(true);
            setLoading(false);
        }
    };

    // Success State
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div
                    className="fixed inset-0 pointer-events-none opacity-40 dark:opacity-20"
                    style={{
                        backgroundImage: 'radial-gradient(hsl(var(--muted-foreground) / 0.3) 1px, transparent 1px)',
                        backgroundSize: '24px 24px',
                    }}
                />
                <div className="w-full max-w-[400px] relative z-10">
                    <div className="bg-card rounded-2xl shadow-xl border border-border p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-xl font-semibold mb-2">Sprawdź swoją skrzynkę!</h2>
                        <p className="text-muted-foreground text-sm mb-6">
                            Wysłaliśmy link aktywacyjny na adres <strong>{email}</strong>.
                            Kliknij w link, aby aktywować konto.
                        </p>
                        <Link to="/login">
                            <Button variant="outline" className="w-full">
                                Powrót do logowania
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            {/* Dot Pattern Background */}
            <div
                className="fixed inset-0 pointer-events-none opacity-40 dark:opacity-20"
                style={{
                    backgroundImage: 'radial-gradient(hsl(var(--muted-foreground) / 0.3) 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                }}
            />

            <div className="w-full max-w-[400px] relative z-10">
                {/* Logo */}
                <div className="text-center mb-8">
                    <h1 className="text-[24px] tracking-[0.15em] font-medium mb-2">WYSKO</h1>
                    <p className="text-sm text-muted-foreground">
                        Utwórz nowe konto
                    </p>
                </div>

                {/* Card */}
                <div className="bg-card rounded-2xl shadow-xl border border-border p-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Error Alert */}
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="break-words">{error}</span>
                            </div>
                        )}

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[13px]">
                                Adres e-mail
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="twoj@email.pl"
                                    className="pl-10 h-10"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-[13px]">
                                Hasło
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Min. 8 znaków"
                                    className="pl-10 pr-10 h-10"
                                    required
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                                Minimum 8 znaków
                            </p>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-[13px]">
                                Potwierdź hasło
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Powtórz hasło"
                                    className="pl-10 h-10"
                                    required
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        {/* Terms of Service */}
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="tos"
                                checked={tosAccepted}
                                onCheckedChange={(checked) => setTosAccepted(checked === true)}
                                className="mt-0.5"
                            />
                            <label htmlFor="tos" className="text-[12px] text-muted-foreground cursor-pointer leading-tight">
                                {checkboxTexts ? (
                                    <>
                                        {checkboxTexts.tos_text.split('{regulamin}')[0]}
                                        <a
                                            href={checkboxTexts.tos_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline hover:text-foreground"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {checkboxTexts.tos_link_label || 'Regulamin'}
                                        </a>
                                        {checkboxTexts.tos_text.split('{regulamin}')[1] || ''}
                                    </>
                                ) : (
                                    <>
                                        {'Akceptuję '}
                                        <a
                                            href="https://www.wysko.pl/regulamin"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="underline hover:text-foreground"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Regulamin
                                        </a>
                                    </>
                                )}
                            </label>
                        </div>

                        {/* Newsletter */}
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="newsletter"
                                checked={newsletterSubscribed}
                                onCheckedChange={(checked) => setNewsletterSubscribed(checked === true)}
                                className="mt-0.5"
                            />
                            <label htmlFor="newsletter" className="text-[12px] text-muted-foreground cursor-pointer leading-tight">
                                {checkboxTexts?.newsletter_text || 'Chcę otrzymywać newsletter z poradami i nowościami'}
                            </label>
                        </div>

                        {/* Submit */}
                        <Button
                            type="submit"
                            className="w-full h-10"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Tworzenie konta...</span>
                                </div>
                            ) : (
                                'Utwórz konto'
                            )}
                        </Button>
                    </form>

                    {/* OAuth Buttons */}
                    <div className="space-y-3 mt-4 mb-6">
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full h-10 font-normal relative"
                            onClick={signInWithGoogle}
                        >
                            <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 absolute left-4" alt="Google" />
                            Kontynuuj z Google
                        </Button>
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[12px] text-muted-foreground">lub</span>
                        <div className="flex-1 h-px bg-border" />
                    </div>

                    {/* Login Link */}
                    <p className="text-center text-[13px] text-muted-foreground">
                        Masz już konto?{' '}
                        <Link
                            to="/login"
                            className="text-foreground font-medium hover:underline"
                        >
                            Zaloguj się
                        </Link>
                    </p>
                </div>

                {/* Footer */}
                <p className="text-center text-[11px] text-muted-foreground mt-6">
                    Rejestrując się, akceptujesz{' '}
                    <a href="#" className="underline hover:text-foreground">
                        Regulamin
                    </a>{' '}
                    i{' '}
                    <a href="#" className="underline hover:text-foreground">
                        Politykę Prywatności
                    </a>
                </p>
            </div>
        </div>
    );
}
